/**
 * Hook: useFormSubmit
 *
 * Centralized form submission with error handling and loading states
 *
 * @param {Object} options - Submit configuration
 * @param {Function} options.onSubmit - Async submit function that receives form values
 * @param {Function} options.onSuccess - Called on successful submit with result
 * @param {Function} options.onError - Called on error with error object
 * @param {Function} options.onRetry - Called when retry is requested
 * @returns {Object} { submit, isSubmitting, error, retry }
 */

import { useCallback, useState } from 'react'

export const useFormSubmit = ({ onSubmit, onSuccess, onError, onRetry }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const submit = useCallback(async (values) => {
    setIsSubmitting(true)
    setError(null)

    try {
      const result = await onSubmit(values)
      if (onSuccess) {
        await onSuccess(result)
      }
      return result
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error(String(err))
      setError(errorObj)
      if (onError) {
        onError(errorObj)
      }
      throw errorObj
    } finally {
      setIsSubmitting(false)
    }
  }, [onSubmit, onSuccess, onError])

  const retry = useCallback(() => {
    if (onRetry) {
      onRetry()
    }
  }, [onRetry])

  return {
    submit,
    isSubmitting,
    error,
    retry
  }
}